/**
 * opportunityStatus — active vs inactive deals, in ONE vocabulary.
 * Card P117 PR-B (Maria, 25 Oct 2026: "clear visual indicators or filters to
 * distinguish active and inactive opportunities").
 *
 * ── ⛔⛔ THE REAL VOCABULARY, NOT A NEW ONE ────────────────────────────────
 *
 * `crm_opportunities.status` holds exactly four values — open / won / lost /
 * abandoned — and the Opportunities tab has rendered them as badges since it
 * was written (`OPP_STATUSES`, OpportunitiesTab.js:20). This module does not
 * invent an "active/inactive" flag beside them; it *derives* that reading
 * from the one column that already exists.
 *
 * ⚠ NOT `crm_opportunities.is_active`. That is the SOFT-DELETE flag and a
 * different idea entirely: a deleted row stays out of every surface whatever
 * its status says. Conflating the two would mean a rep "reactivating" a deal
 * by un-deleting it. The two are deliberately never mixed here, and the board
 * handler still filters `is_active = TRUE` unconditionally.
 *
 * ── ⚠ WHY THE COLOURS LIVE HERE AND NOT IN EACH VIEW ──────────────────────
 *
 * The list already had badge colours; the board had nothing. Copying the list's
 * palette into the board is how two screens start disagreeing about what amber
 * means. One table, imported by both.
 */
"use strict";

/** The four values the column can hold. Order is the filter-chip order. */
export const OPPORTUNITY_STATUSES = ['open', 'won', 'lost', 'abandoned'];

/**
 * ⚠ Matches OpportunitiesTab's existing OPP_STATUSES palette exactly, so the
 * list and the board show the same colour for the same word.
 */
export const STATUS_META = {
  open:      { label: 'Open',      color: '#2E8B98', bg: '#E0F2FE', active: true },
  won:       { label: 'Won',       color: '#4FA038', bg: '#DCFCE7', active: false },
  lost:      { label: 'Lost',      color: '#DC3545', bg: '#FEE2E2', active: false },
  abandoned: { label: 'Abandoned', color: '#6C757D', bg: '#F3F4F6', active: false },
};

/** Unknown/missing reads as 'open' — the column's own default. */
export function normalizeStatus(status) {
  const s = typeof status === 'string' ? status.trim().toLowerCase() : '';
  return OPPORTUNITY_STATUSES.includes(s) ? s : 'open';
}

export function statusMeta(status) {
  return STATUS_META[normalizeStatus(status)];
}

/**
 * Is this deal still live work?
 *
 * ⚠ "Inactive" is Maria's word and it covers THREE different endings — won,
 * lost and abandoned. They are not the same thing to a rep and they keep
 * their own badges; this predicate exists only for the muting rule, which
 * asks one question: is there anything left to do here.
 */
export function isActiveStatus(status) {
  return statusMeta(status).active === true;
}

/**
 * The muting style for a card or row.
 *
 * ⚠ MUTED, NEVER HIDDEN. A closed deal still answers "what happened to this
 * one?", which is the question Maria is asking when she scans the board. It
 * is dimmed so the eye skips it, and it keeps its badge so the eye can come
 * back. Opacity alone would be a colour-only signal — the badge carries the
 * word.
 */
export function mutedStyle(status) {
  return isActiveStatus(status) ? null : { opacity: 0.55, filter: 'saturate(0.6)' };
}

/**
 * A plain-language summary of what a status selection is hiding.
 *
 * ⛔⛔ THIS EXISTS BECAUSE THE BOARD WAS SILENTLY FILTERED. Both board
 * handlers hard-coded `status = 'open'`, so won/lost/abandoned deals were not
 * muted — they were ABSENT, with nothing on screen admitting it. A board that
 * hides rows without saying so teaches people to trust a count that is not
 * the whole count. The default is unchanged; what changes is that it now
 * says what it is.
 *
 * @returns {string|null} null when nothing is excluded
 */
export function hiddenStatusNote(selected) {
  const picked = Array.isArray(selected) && selected.length
    ? selected.filter((s) => OPPORTUNITY_STATUSES.includes(s))
    : ['open'];
  const hidden = OPPORTUNITY_STATUSES.filter((s) => !picked.includes(s));
  if (!hidden.length) return null;
  const names = hidden.map((s) => STATUS_META[s].label.toLowerCase());
  const list = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Hiding ${list} deals`;
}
